import React from "react";
import ReactDOMServer from "react-dom/server";
import utilFunctions from "../../../utilities/utilFunctions";
import { recipeIngredientTypes } from "../../../clients/inventoryObjects";

const RecipePreviewContent = ({
  recipe,
  productLibraryMap,
  currencySymbol,
  locale,
  currency,
  options = {},
}) => {
  const {
    showCostingInfo = true,
    showLaborInfo = true,
    showIngredients = true,
    showTrainingDetails = true,
  } = options;

  const calculateTotalCost = () => {
    let total = 0;

    recipe?.ingredients?.forEach((x) => {
      if (x.ingredientCost?.isValid && x.includeInCosting) {
        total += x.ingredientCost?.result?.amount || 0;
        total += x.laborCost?.result?.amount || 0;
      }
    });

    total += recipe?.laborCost?.cost?.amount || 0;

    return utilFunctions.convertCentsToLargerFormatCurrency(
      total,
      locale,
      currency,
    );
  };

  const calculateIngredientCostTotal = () => {
    let total = 0;

    recipe?.ingredients?.forEach((x) => {
      if (x.ingredientCost?.isValid && x.includeInCosting) {
        total += x.ingredientCost?.result?.amount || 0;
      }
    });

    return utilFunctions.convertCentsToLargerFormatCurrency(
      total,
      locale,
      currency,
    );
  };

  const getIngredientName = (ingredient) => {
    if (ingredient.menuDisplayName) {
      return ingredient.menuDisplayName;
    }
    if (ingredient.type === recipeIngredientTypes.Recipe) {
      return ingredient.recipeIngredientReference?.name || "Recipe";
    } else if (ingredient.productId) {
      const product = productLibraryMap?.[ingredient.productId];
      return product?.name || "Product";
    }
    return "Unknown";
  };

  const formatYield = () => {
    const yieldDisplay = utilFunctions.extractRecipeYieldSizeDisplay(recipe);
    return yieldDisplay || "Not set";
  };

  const formatLaborDuration = () => {
    const hours = recipe?.laborCost?.hourDuration || 0;
    const minutes = recipe?.laborCost?.minuteDuration || 0;
    if (hours === 0 && minutes === 0) return "Not set";
    const hourStr = hours < 10 ? `0${hours}` : hours;
    const minStr = minutes < 10 ? `0${minutes}` : minutes;
    return `${hourStr}:${minStr}`;
  };

  // Theme values (from src/Theme/palette.js + typograpghy.js)
  const palette = {
    primaryAccent: "#C74822", // primary[800]
    primaryLight: "#F1A498", // primary[200]
    textPrimary: "#282828", // terrain[900] / terrain["01"]
    textSecondary: "#7E7E7E", // terrain[600] / terrain["02"]
    border: "#C6C6C6", // terrain[400] / terrain["03"]
    bgLight: "#F6F6F6", // terrain[100] / terrain["04"]
    bgPage: "#FBFBFB", // terrain[50]
  };

  const styles = {
    container: {
      fontFamily: "Inter, Arial, sans-serif",
      maxWidth: "800px",
      margin: "0 auto",
      padding: "24px",
      color: palette.textPrimary,
      backgroundColor: palette.bgPage,
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      paddingBottom: "16px",
      marginBottom: "20px",
    },
    image: {
      width: "140px",
      height: "140px",
      objectFit: "cover",
      borderRadius: "6px",
      flexShrink: 0,
    },
    title: {
      fontFamily: "Quicksand, Arial, sans-serif",
      fontSize: "28px",
      fontWeight: 700,
      marginBottom: "4px",
      color: palette.textPrimary,
    },
    yieldText: {
      fontFamily: "Inter, Arial, sans-serif",
      fontSize: "13px",
      color: palette.textSecondary,
    },
    section: {
      marginBottom: "24px",
    },
    sectionTitle: {
      fontFamily: "Quicksand, Arial, sans-serif",
      fontSize: "16px",
      fontWeight: 700,
      marginBottom: "10px",
      color: palette.textPrimary,
    },
    infoGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "8px",
    },
    infoBox: {
      borderRadius: "8px",
      padding: "10px 14px",
      textAlign: "center",
      backgroundColor: palette.bgLight,
    },
    infoBoxLast: {
      borderRadius: "8px",
      padding: "10px 14px",
      textAlign: "center",
      backgroundColor: palette.bgLight,
    },
    infoLabel: {
      fontFamily: "Quicksand, Arial, sans-serif",
      fontSize: "13px",
      fontWeight: 700,
      color: palette.textPrimary,
      marginBottom: "6px",
    },
    infoValue: {
      fontFamily: "Inter, Arial, sans-serif",
      fontSize: "14px",
      fontWeight: 700,
      color: palette.textPrimary,
    },
    costingGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "8px",
    },
    costBox: {
      borderRadius: "8px",
      padding: "10px",
      textAlign: "center",
      backgroundColor: palette.bgLight,
    },
    costBoxLast: {
      borderRadius: "8px",
      padding: "10px",
      textAlign: "center",
      backgroundColor: palette.bgLight,
    },
    costLabel: {
      fontFamily: "Quicksand, Arial, sans-serif",
      fontSize: "13px",
      fontWeight: 700,
      color: palette.textPrimary,
      marginBottom: "6px",
      textTransform: "none",
    },
    costValue: {
      fontFamily: "Inter, Arial, sans-serif",
      fontSize: "14px",
      fontWeight: 600,
      color: palette.textPrimary,
    },
    tableWrapper: {
      borderRadius: "8px",
      overflow: "hidden",
      border: `1px solid ${palette.border}`,
      marginBottom: "20px",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "13px",
      fontFamily: "Inter, Arial, sans-serif",
    },
    th: {
      backgroundColor: palette.bgLight,
      borderBottom: `1px solid ${palette.border}`,
      padding: "8px 10px",
      textAlign: "left",
      fontWeight: 700,
      fontSize: "11px",
      color: palette.textPrimary,
      textTransform: "none",
    },
    td: {
      borderBottom: `1px solid ${palette.border}`,
      padding: "8px 10px",
      color: palette.textPrimary,
    },
    trainingSection: {
      marginBottom: "16px",
    },
    trainingLabel: {
      fontFamily: "Quicksand, Arial, sans-serif",
      fontSize: "14px",
      fontWeight: 700,
      marginBottom: "6px",
      color: palette.textPrimary,
    },
    trainingContent: {
      fontFamily: "Inter, Arial, sans-serif",
      fontSize: "13px",
      lineHeight: "1.6",
      whiteSpace: "pre-wrap",
      color: palette.textSecondary,
    },
  };

  // Determine which columns to show in ingredients table based on options
  const showCostColumn = showCostingInfo;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        {recipe?.imageUrl && (
          <img src={recipe.imageUrl} alt={recipe.name} style={styles.image} />
        )}
        <div>
          <div style={styles.title}>
            {utilFunctions.capitalizeString(recipe?.name || "")}
          </div>
          <div style={styles.yieldText}>Yield: {formatYield()}</div>
        </div>
      </div>

      {/* Labor Info */}
      {showLaborInfo && (
        <div style={styles.section}>
          <div style={styles.infoGrid}>
            <div style={styles.infoBox}>
              <div style={styles.infoLabel}>Labor Wage</div>
              <div style={styles.infoValue}>
                {currencySymbol}
                {utilFunctions.convertCentsToLargerFormatCurrency(
                  recipe?.laborCost?.wage?.amount || 0,
                  locale,
                  currency,
                )}
              </div>
            </div>
            <div style={styles.infoBox}>
              <div style={styles.infoLabel}>Labor Duration</div>
              <div style={styles.infoValue}>{formatLaborDuration()}</div>
            </div>
            <div style={styles.infoBox}>
              <div style={styles.infoLabel}>Labor Cost</div>
              <div style={styles.infoValue}>
                {currencySymbol}
                {utilFunctions.convertCentsToLargerFormatCurrency(
                  recipe?.laborCost?.cost?.amount || 0,
                  locale,
                  currency,
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Costing Summary */}
      {showCostingInfo && (
        <div style={styles.section}>
          <div style={styles.costingGrid}>
            <div style={styles.costBox}>
              <div style={styles.costLabel}>Menu Price</div>
              <div style={styles.costValue}>
                {currencySymbol}
                {utilFunctions.convertCentsToLargerFormatCurrency(
                  recipe?.approximateMenuPrice?.amount || 0,
                  locale,
                  currency,
                )}
              </div>
            </div>
            <div style={styles.costBox}>
              <div style={styles.costLabel}>Ingredient Cost</div>
              <div style={styles.costValue}>
                {currencySymbol}
                {calculateIngredientCostTotal()}
              </div>
            </div>
            <div style={styles.costBox}>
              <div style={styles.costLabel}>Total Cost</div>
              <div style={styles.costValue}>
                {currencySymbol}
                {calculateTotalCost()}
              </div>
            </div>
            <div style={styles.costBox}>
              <div style={styles.costLabel}>Est. Profit</div>
              <div style={styles.costValue}>
                {currencySymbol}
                {utilFunctions.convertCentsToLargerFormatCurrency(
                  (recipe?.approximateMenuPrice?.amount || 0) -
                    (() => {
                      let total = 0;
                      recipe?.ingredients?.forEach((x) => {
                        if (x.ingredientCost?.isValid && x.includeInCosting) {
                          total += x.ingredientCost?.result?.amount || 0;
                          total += x.laborCost?.result?.amount || 0;
                        }
                      });
                      total += recipe?.laborCost?.cost?.amount || 0;
                      return total;
                    })(),
                  locale,
                  currency,
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ingredients Table */}
      {showIngredients && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Ingredients</div>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Quantity</th>
                  <th style={styles.th}>Ingredient Name</th>
                  <th style={styles.th}>Garnish</th>
                  {showCostColumn && <th style={styles.th}>Cost</th>}
                </tr>
              </thead>
              <tbody>
                {recipe?.ingredients?.map((ingredient, index, arr) => {
                  const isLast = !showCostColumn && index === arr.length - 1;
                  const tdStyle = isLast
                    ? { ...styles.td, borderBottom: "none" }
                    : styles.td;
                  return (
                    <tr key={ingredient.id || index}>
                      <td style={tdStyle}>
                        {ingredient.quantity?.amount || "-"}{" "}
                        {ingredient.quantity?.unitOfMeasurement?.name || ""}
                      </td>
                      <td style={tdStyle}>
                        {utilFunctions.capitalizeString(
                          getIngredientName(ingredient),
                        )}
                      </td>
                      <td style={tdStyle}>
                        {ingredient.isGarnish ? "Yes" : "No"}
                      </td>
                      {showCostColumn && (
                        <td style={styles.td}>
                          {ingredient.ingredientCost?.isValid
                            ? `${currencySymbol}${utilFunctions.convertCentsToLargerFormatCurrency(
                                ingredient.ingredientCost?.result?.amount || 0,
                                locale,
                                currency,
                              )}`
                            : "-"}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {showCostColumn && (
                <tfoot>
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        ...styles.td,
                        fontWeight: "bold",
                        textAlign: "left",
                        borderBottom: "none",
                        backgroundColor: palette.bgLight,
                      }}
                    >
                      Total
                    </td>
                    <td
                      style={{
                        ...styles.td,
                        fontWeight: "bold",
                        borderBottom: "none",
                        backgroundColor: palette.bgLight,
                      }}
                    >
                      {currencySymbol}
                      {calculateIngredientCostTotal()}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Training Details */}
      {showTrainingDetails && (
        <div style={styles.section}>
          <div style={styles.trainingSection}>
            <div style={styles.trainingLabel}>Description</div>
            <div style={styles.trainingContent}>
              {recipe?.description || "No description provided"}
            </div>
          </div>
          <div style={styles.trainingSection}>
            <div style={styles.trainingLabel}>Tasting Notes</div>
            <div style={styles.trainingContent}>
              {recipe?.tastingNotes || "No tasting notes provided"}
            </div>
          </div>
          <div style={styles.trainingSection}>
            <div style={styles.trainingLabel}>Directions</div>
            <div style={styles.trainingContent}>
              {recipe?.directions || "No directions provided"}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div
        style={{
          textAlign: "center",
          fontSize: "10px",
          color: "#999",
          marginTop: "40px",
          paddingTop: "20px",
          borderTop: "1px solid #ddd",
        }}
      >
        Generated on {new Date().toLocaleDateString()}
      </div>
    </div>
  );
};

export const openRecipePreview = ({
  recipe,
  productLibraryMap,
  currencySymbol,
  locale,
  currency,
  options = {},
}) => {
  const htmlContent = ReactDOMServer.renderToStaticMarkup(
    <RecipePreviewContent
      recipe={recipe}
      productLibraryMap={productLibraryMap}
      currencySymbol={currencySymbol}
      locale={locale}
      currency={currency}
      options={options}
    />,
  );

  const printStyles = `
    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      @page {
        margin: 0.5in;
      }
    }
    body {
      margin: 0;
      padding: 0;
      background-color: #FBFBFB;
    }
    .print-button {
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 10px 20px;
      background-color: #C74822;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 400;
      font-family: Quicksand, Arial, sans-serif;
    }
    .print-button:hover {
      background-color: #EF6B4B;
    }
    @media print {
      .print-button {
        display: none;
      }
    }
  `;

  const fullHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${recipe?.name || "Recipe"} - Preview</title>
        <meta charset="utf-8">
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&family=Quicksand:wght@400;700&display=swap');
          ${printStyles}
        </style>
      </head>
      <body>
        <button class="print-button" onclick="window.print()">Print Recipe</button>
        ${htmlContent}
      </body>
    </html>
  `;

  const previewWindow = window.open("", "_blank");
  if (previewWindow) {
    previewWindow.document.write(fullHtml);
    previewWindow.document.close();
  }
};

export default RecipePreviewContent;
